import type { ChangeEvent } from 'react';
import moment from 'moment';
import { DatePicker } from 'components/date-picker/date-picker';
import { DateRangeField } from 'components/dynamic-date-filter';
import type { DateRangeFilterField, FilterTemplateProps } from '../../types';
import { useCategoryFields } from '../template-hooks';
import { useIsFilterRail } from '../context';
import { FieldLabel } from '../field-label';
import style from '../filter-modal.module.scss';

interface DateRangeRowProps {
  field: DateRangeFilterField;
  formValues: Record<string, unknown>;
  onFormChange: (key: string, value: unknown) => void;
}

/**
 * Stages a typed date. flatpickr's onChange only fires on calendar-select or
 * Enter, so a typed date is lost on blur; this commits once a full date parses
 * (or clears on empty), so typing behaves like picking.
 * @param {string} param The date param.
 * @param {Function} onFormChange Stages the value.
 * @return {Function} The input's change handler.
 */
function manualInputHandler(
  param: string,
  onFormChange: (key: string, value: unknown) => void
) {
  return (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.trim();
    if (raw === '') {
      onFormChange(param, undefined);
      return;
    }
    const parsed = moment(raw, ['MM/DD/YYYY', 'M/D/YYYY'], true);
    if (parsed.isValid()) {
      onFormChange(param, parsed.format('YYYY-MM-DD'));
    }
  };
}

/**
 * Stages a date picked from the calendar, or clears it.
 * @param {string} param The date param.
 * @param {Function} onFormChange Stages the value.
 * @return {Function} The picker's change handler.
 */
function pickedDateHandler(
  param: string,
  onFormChange: (key: string, value: unknown) => void
) {
  return ([date]: Date[]) =>
    onFormChange(param, date ? moment(date).format('YYYY-MM-DD') : undefined);
}

/**
 * One bound of a range as a date picker.
 * @param {object} props The picker.
 * @param {string} props.param The date param.
 * @param {string} props.labelText "From" or "To".
 * @param {Record<string, unknown>} props.formValues The staged filters.
 * @param {Function} props.onFormChange Stages a value.
 * @return {JSX.Element} The picker.
 */
function RangeBound({
  param,
  labelText,
  formValues,
  onFormChange,
}: {
  param: string;
  labelText: string;
  formValues: Record<string, unknown>;
  onFormChange: (key: string, value: unknown) => void;
}) {
  return (
    <DatePicker
      id={`${param}-input`}
      labelText={labelText}
      size="md"
      value={(formValues[param] as string) ?? ''}
      onInputChange={manualInputHandler(param, onFormChange)}
      onChange={pickedDateHandler(param, onFormChange)}
    />
  );
}

/**
 * A range on one line: "From [date] To [date]", as the filter modal lays it.
 * @param {DateRangeRowProps} props The range.
 * @return {JSX.Element} The range.
 */
function DateRangeRow({ field, formValues, onFormChange }: DateRangeRowProps) {
  return (
    <div className={style.rangeRow}>
      <h6 className={style.rangeLabel}>{field.label}</h6>
      <div className={style.rangeInputs}>
        <span className={style.rangeInlineLabel}>From</span>
        <RangeBound
          param={field.minParam}
          labelText="From"
          formValues={formValues}
          onFormChange={onFormChange}
        />
        <span className={style.rangeInlineLabel}>To</span>
        <RangeBound
          param={field.maxParam}
          labelText="To"
          formValues={formValues}
          onFormChange={onFormChange}
        />
      </div>
    </div>
  );
}

/**
 * A compact range: the shared date range at its small size, labelled by the
 * filter, with a Clear for this range alone.
 * @param {DateRangeRowProps} props The range.
 * @return {JSX.Element} The range.
 */
function CompactDateRange({
  field,
  formValues,
  onFormChange,
}: DateRangeRowProps) {
  return (
    <DateRangeField
      id={field.id}
      label={<FieldLabel field={field} />}
      clearLabel={`Clear ${field.label}`}
      size="sm"
      labelledGroup
      start={formValues[field.minParam] as string | undefined}
      end={formValues[field.maxParam] as string | undefined}
      onChange={(start, end) => {
        onFormChange(field.minParam, start);
        onFormChange(field.maxParam, end);
      }}
      onClear={() => {
        onFormChange(field.minParam, undefined);
        onFormChange(field.maxParam, undefined);
      }}
    />
  );
}

/**
 * The category's date-range filters.
 * @param {FilterTemplateProps} props The template props.
 * @return {JSX.Element} The ranges.
 */
export function DateRangeTemplate({
  category,
  formValues,
  onFormChange,
}: FilterTemplateProps) {
  const fields = useCategoryFields(category, 'date-range');
  const Range = useIsFilterRail() ? CompactDateRange : DateRangeRow;

  return (
    <div>
      {fields.map((field) => (
        <Range
          key={field.id}
          field={field}
          formValues={formValues}
          onFormChange={onFormChange}
        />
      ))}
    </div>
  );
}
